/**
 * College Cost Planner tile (BUILD-SPEC-2 §6.7): projects the future cost of
 * college at an assumed education-inflation rate and solves for the level
 * monthly amount to fund it by the start date, counting what's already saved.
 * Deterministic; the inflation and return rates are the user's clearly labeled
 * assumptions, never a forecast. Pairs with the FAFSA Student Aid Index estimator
 * for the aid side. Information, not advice.
 */
import { Money } from "../engine/money";
import { clampYears, collegeCostPlan } from "../engine/finance";
import { el } from "../ui/dom";
import { assumptionHints, field, parseNonNegative, pctPoints, tryExampleButton } from "../ui/form";
import { resultCard, type BreakdownLine } from "../ui/resultCard";
import { sensitivityTable, sensitivityToggle } from "../ui/sensitivity";
import type { TileContext, TileDefinition } from "./types";

/** How far the opt-in range flexes the inflation assumption, in percentage points. */
const INFLATION_DELTA = 2;

/** Defensible bands for the two forecast assumptions; outside either, one calm
 *  combined hint signposts a stress scenario (SPEC-3 §2.4). Never a clamp. */
const INFLATION_BAND = { low: 0, high: 20, label: "College cost inflation" };
const RETURN_BAND = { low: -50, high: 50, label: "Expected return" };

interface Fields {
  annualCostToday: number;
  yearsUntilStart: number;
  yearsOfCollege: number;
  costInflationPct: number;
  currentSavings: number;
  expectedReturnPct: number;
  /** Show the opt-in low/base/high range on the inflation assumption. */
  band: boolean;
}

const EXAMPLE: Fields = {
  annualCostToday: 28000,
  yearsUntilStart: 10,
  yearsOfCollege: 4,
  costInflationPct: 5,
  currentSavings: 10000,
  expectedReturnPct: 5,
  band: false,
};

/**
 * Both year counts are clamped here for the reason the projection line needs:
 * it reads "Projected cost (4 yr, in 10 yr)" from these fields while
 * {@link collegeCostPlan} projects from the clamped pair. `?yrs=1e15&dur=1e15`
 * printed both horizons at full length over a 100-year answer.
 */
function readFields(p: URLSearchParams): Fields {
  return {
    annualCostToday: parseNonNegative(p.get("cost"), 0),
    yearsUntilStart: clampYears(parseNonNegative(p.get("yrs"), 10)),
    yearsOfCollege: Math.max(1, clampYears(parseNonNegative(p.get("dur"), 4))),
    costInflationPct: parseNonNegative(p.get("ci"), 5),
    currentSavings: parseNonNegative(p.get("c"), 0),
    expectedReturnPct: parseNonNegative(p.get("r"), 5),
    band: p.get("band") === "1",
  };
}

function writeFields(f: Fields): URLSearchParams {
  const p = new URLSearchParams();
  p.set("cost", String(f.annualCostToday));
  p.set("yrs", String(f.yearsUntilStart));
  if (f.yearsOfCollege !== 4) p.set("dur", String(f.yearsOfCollege));
  if (f.costInflationPct !== 5) p.set("ci", String(f.costInflationPct));
  if (f.currentSavings > 0) p.set("c", String(f.currentSavings));
  if (f.expectedReturnPct !== 5) p.set("r", String(f.expectedReturnPct));
  if (f.band) p.set("band", "1");
  return p;
}

/** The required monthly contribution at a given inflation rate — same evaluation. */
function monthlyAt(fields: Fields, ciPct: number): Money {
  return collegeCostPlan({
    annualCostToday: fields.annualCostToday,
    yearsUntilStart: fields.yearsUntilStart,
    yearsOfCollege: fields.yearsOfCollege,
    costInflationPct: ciPct,
    currentSavings: fields.currentSavings,
    expectedReturnPct: fields.expectedReturnPct,
  }).monthlyContribution;
}

export function mountCollegeCost(ctx: TileContext): void {
  const { root } = ctx;
  root.replaceChildren();
  let fields = readFields(ctx.params);

  const mkNum = (name: string, label: string, value: number, step: number): HTMLInputElement =>
    el("input", {
      type: "number",
      name,
      min: 0,
      step,
      value,
      attrs: { "aria-label": label, inputmode: "decimal" },
    });
  const costInput = mkNum("cost", "Annual college cost today", fields.annualCostToday, 1000);
  const yrsInput = mkNum("yrs", "Years until college starts", fields.yearsUntilStart, 1);
  const durInput = mkNum("dur", "Years of college", fields.yearsOfCollege, 1);
  const ciInput = mkNum("ci", "College cost inflation (percent)", fields.costInflationPct, 0.5);
  const cInput = mkNum("c", "Already saved", fields.currentSavings, 1000);
  const rInput = mkNum("r", "Expected return (percent)", fields.expectedReturnPct, 0.5);

  const resultContainer = el("div", { class: "tile-result", attrs: { "aria-live": "polite" } });

  function compute(): void {
    resultContainer.replaceChildren();
    if (fields.annualCostToday <= 0) {
      resultContainer.append(
        el("p", { class: "ph-empty", text: "Enter today's annual college cost to plan for it." }),
      );
      return;
    }
    const r = collegeCostPlan({
      annualCostToday: fields.annualCostToday,
      yearsUntilStart: fields.yearsUntilStart,
      yearsOfCollege: fields.yearsOfCollege,
      costInflationPct: fields.costInflationPct,
      currentSavings: fields.currentSavings,
      expectedReturnPct: fields.expectedReturnPct,
    });
    const fmt = (m: Money): string => m.format(ctx.locale);

    const lines: BreakdownLine[] = [
      {
        label: `Projected cost (${fields.yearsOfCollege} yr, in ${fields.yearsUntilStart} yr)`,
        value: fmt(r.projectedTotalCost),
      },
      { label: "Your savings grow to", value: fmt(r.projectedFromCurrent) },
      {
        label: "Where you stand",
        value: r.alreadyOnTrack
          ? "Your current savings are on track to cover the projected cost."
          : `Save ${fmt(r.monthlyContribution)}/mo to fully fund it by the start date.`,
        emphasis: true,
      },
      {
        label: "Assumptions",
        value: `${pctPoints(fields.costInflationPct)} college inflation, ${pctPoints(fields.expectedReturnPct)} return, both yours to change. Targets the full cost by freshman year.`,
      },
    ];

    resultContainer.append(
      resultCard({
        label: "Save each month for college",
        value: r.monthlyContribution,
        locale: ctx.locale,
        breakdown: lines,
        permalink: () => ctx.permalink(writeFields(fields)),
      }),
    );

    const hint = assumptionHints([
      { valuePct: fields.costInflationPct, band: INFLATION_BAND },
      { valuePct: fields.expectedReturnPct, band: RETURN_BAND },
    ]);
    if (hint) resultContainer.append(hint);

    if (fields.band) {
      const low = Math.max(0, fields.costInflationPct - INFLATION_DELTA);
      const high = fields.costInflationPct + INFLATION_DELTA;
      resultContainer.append(
        sensitivityTable(
          `If college inflation runs ${INFLATION_DELTA} points either side of your ${pctPoints(fields.costInflationPct)} assumption:`,
          [
            {
              label: "Lower inflation",
              assumption: `${low}%`,
              result: fmt(monthlyAt(fields, low)),
            },
            {
              label: "Your assumption",
              assumption: pctPoints(fields.costInflationPct),
              result: fmt(r.monthlyContribution),
              base: true,
            },
            {
              label: "Higher inflation",
              assumption: `${high}%`,
              result: fmt(monthlyAt(fields, high)),
            },
          ],
        ),
      );
    }
  }

  const bandToggle = sensitivityToggle(
    "Show a range (±2 points on inflation)",
    fields.band,
    (on) => {
      fields = { ...fields, band: on };
      ctx.setParams(writeFields(fields));
      compute();
    },
  );

  function recompute(): void {
    fields = {
      annualCostToday: parseNonNegative(costInput.value, 0),
      yearsUntilStart: clampYears(parseNonNegative(yrsInput.value, 10)),
      yearsOfCollege: Math.max(1, clampYears(parseNonNegative(durInput.value, 4))),
      costInflationPct: parseNonNegative(ciInput.value, 5),
      currentSavings: parseNonNegative(cInput.value, 0),
      expectedReturnPct: parseNonNegative(rInput.value, 5),
      band: bandToggle.querySelector("input")!.checked,
    };
    ctx.setParams(writeFields(fields));
    compute();
  }

  for (const i of [costInput, yrsInput, durInput, ciInput, cInput, rInput])
    i.addEventListener("input", recompute);

  const tryExample = tryExampleButton(() => {
    fields = { ...EXAMPLE };
    costInput.value = String(fields.annualCostToday);
    yrsInput.value = String(fields.yearsUntilStart);
    durInput.value = String(fields.yearsOfCollege);
    ciInput.value = String(fields.costInflationPct);
    cInput.value = String(fields.currentSavings);
    rInput.value = String(fields.expectedReturnPct);
    bandToggle.querySelector("input")!.checked = fields.band;
    recompute();
  });

  const form = el(
    "form",
    { class: "tile-form", on: { submit: (e) => e.preventDefault() } },
    field("Annual college cost today", costInput),
    field("Years until college starts", yrsInput),
    field("Years of college", durInput),
    field("College cost inflation (%)", ciInput),
    field("Already saved", cInput),
    field("Expected return (%)", rInput),
    bandToggle,
    el("div", { class: "tile-form-actions" }, tryExample),
  );

  root.append(form, resultContainer);
  compute();
}

export const collegeCostTile: TileDefinition = {
  id: "college-cost",
  title: "College Cost Planner",
  pillar: "protect",
  description: "Project college costs and the monthly savings to fund them.",
  keywords: ["college", "529", "education", "tuition", "savings", "cost"],
  status: "ready",
  how: "College costs tend to rise faster than general inflation, so a price today understates the bill years from now. This grows each future year of college at the inflation rate you choose, adds them up, and solves for the level monthly amount that would fully fund the total by the time college starts, counting what you've already saved and an assumed return on it.\n\nThe inflation and return rates are your assumptions, shown so you can change them; we never forecast markets. To keep it simple and a touch conservative, it targets having the whole cost saved by freshman year, though in reality you draw it down across the college years. Pair it with the FAFSA Student Aid Index estimator to weigh grants and aid against the sticker price.\n\nFederal borrowing has new limits from July 1, 2026. A parent can borrow PLUS loans of up to $20,000 a year and $65,000 in all for each dependent student. A graduate student can borrow $20,500 a year and $100,000 in all, a professional student $50,000 and $200,000, and Grad PLUS loans are no longer made. No student can borrow more than $257,500 in federal loans over a lifetime. A student already enrolled and borrowing on June 30, 2026 keeps the old limits for up to three years. So the gap between the cost and what you have saved can be harder to borrow than it was.",
  resources: [
    { label: "Federal Student Aid", url: "https://studentaid.gov/" },
    {
      label: "SEC, saving for college (529 plans)",
      url: "https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-bulletins/introduction-529-plans-investor-bulletin",
    },
  ],
  related: [
    {
      hubId: "investing",
      tool: "trump-account",
      label: "Trump Account (child savings)",
      note: "the 2026 account for a child under 18, and what it costs in tax at 18",
    },
  ],
  mount: mountCollegeCost,
};
